/**
 * IT > Service Desk > Knowledge base (/it/service-desk/kb). Every help article,
 * drafts included. The Help button shows only published articles, and only to
 * the audiences ticked on each one.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyLinkButton, gridTheadClassName } from '@ampersandbrands/ui';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { RelativeTime } from '@/lib/RelativeTime';
import { buildCurrentArticleLink } from '@/lib/articleLink';
import { useArticlesStaff } from '@/lib/useServiceDesk';
import type { SdArticleCategory } from '@/lib/serviceDeskTypes';
import { SdShell } from './SdShell';
import { ARTICLE_STATUS_LABELS, ARTICLE_STATUS_TONES, ARTICLE_TYPE_LABELS, AUDIENCE_LABELS } from './sdLabels';
import { SD_BTN_PRIMARY, SD_INPUT, SD_MUTED } from './sdStyles';

function CategoriesCell({ categories }: { categories: SdArticleCategory[] }) {
  if (categories.length === 0) return <>--</>;
  if (categories.length <= 2) return <>{categories.map((c) => c.path).join(', ')}</>;
  return (
    <span title={categories.map((c) => c.path).join('; ')} className="cursor-help underline decoration-dotted">
      {categories.length} categories
    </span>
  );
}

export function KnowledgeBase() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const articles = useArticlesStaff({ status, q });
  const navigate = useNavigate();
  const rows = articles.data ?? [];

  return (
    <SdShell
      title="Knowledge base"
      subtitle="Help articles people see in the Help button. Write one, publish it, and retire it when it goes out of date."
      tab="kb"
      actions={
        <Link to="/it/service-desk/kb/new" className={SD_BTN_PRIMARY}>
          New article
        </Link>
      }
    >
      <DataGridShell
        total={rows.length}
        loaded={rows.length}
        countNoun="articles"
        activeFilterCount={(status ? 1 : 0) + (q ? 1 : 0)}
        toolbar={
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <FilterLabel htmlFor="sd-kb-status">Status</FilterLabel>
              <select id="sd-kb-status" value={status} onChange={(e) => setStatus(e.target.value)} className={SD_INPUT}>
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div className="sm:col-span-1 lg:col-span-2">
              <FilterLabel htmlFor="sd-kb-search">Search</FilterLabel>
              <ClearableSearchInput
                id="sd-kb-search"
                value={q}
                onChange={setQ}
                placeholder="Title, summary, or words in the article"
                className={SD_INPUT}
              />
            </div>
          </div>
        }
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {['Title', 'Type', 'Status', 'Shown to', 'Categories', 'Updated', 'Link'].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {articles.isLoading ? (
              <tr>
                <td colSpan={7} className={`px-4 py-6 ${SD_MUTED}`}>
                  Loading articles...
                </td>
              </tr>
            ) : articles.error ? (
              <tr>
                <td colSpan={7} className={`px-4 py-6 ${SD_MUTED}`}>
                  The knowledge base is not available yet.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className={`px-4 py-6 ${SD_MUTED}`}>
                  No articles match. Use New article to write the first one.
                </td>
              </tr>
            ) : (
              rows.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => navigate(`/it/service-desk/kb/${a.id}`)}
                  className="cursor-pointer hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
                >
                  <td className="px-3 py-2 align-top">
                    <Link
                      to={`/it/service-desk/kb/${a.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-amp-olive-deep hover:underline dark:text-amp-olive-dark"
                    >
                      {a.title}
                    </Link>
                    {a.summary && <p className={`text-[12px] ${SD_MUTED}`}>{a.summary}</p>}
                  </td>
                  <td className="px-3 py-2 align-top">{ARTICLE_TYPE_LABELS[a.article_type] ?? a.article_type}</td>
                  <td className="px-3 py-2 align-top">
                    <Pill tone={ARTICLE_STATUS_TONES[a.status]}>{ARTICLE_STATUS_LABELS[a.status] ?? a.status}</Pill>
                  </td>
                  <td className="px-3 py-2 align-top">{a.audience.map((x) => AUDIENCE_LABELS[x] ?? x).join(', ')}</td>
                  <td className="px-3 py-2 align-top">
                    <CategoriesCell categories={a.categories ?? []} />
                  </td>
                  <td className={`whitespace-nowrap px-3 py-2 align-top ${SD_MUTED}`}><RelativeTime value={a.updated_at} /></td>
                  <td className="px-3 py-1 align-top" onClick={(e) => e.stopPropagation()}>
                    {a.status === 'published' ? (
                      (() => {
                        const link = buildCurrentArticleLink(a);
                        return link ? <CopyLinkButton url={link} /> : null;
                      })()
                    ) : (
                      <span className={SD_MUTED}>--</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </SdShell>
  );
}
